import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tag, Plus, X, Trash2, Percent, DollarSign, Clock, Users,
  ToggleLeft, ToggleRight, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

/**
 * Shared CouponManager component for Admin and Coach dashboards
 * Handles coupon CRUD operations with Stripe integration
 */
const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [couponForm, setCouponForm] = useState({
    name: "",
    discount_type: "percent",
    discount_value: "",
    duration: "once",
    duration_in_months: "",
    max_redemptions: "",
    promo_code: "",
    applies_to_tiers: ["foundations", "accelerator", "elite"]
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/payments/coupons`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data.coupons || []);
      } else {
        toast.error("Failed to load coupons");
      }
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCouponForm({
      name: "",
      discount_type: "percent",
      discount_value: "",
      duration: "once",
      duration_in_months: "",
      max_redemptions: "",
      promo_code: "",
      applies_to_tiers: ["foundations", "accelerator", "elite"]
    });
  };

  const handleCreateCoupon = async (e) => {
    if (e) e.preventDefault();
    
    if (!couponForm.name || !couponForm.discount_value) {
      toast.error("Please fill in coupon name and discount value");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/payments/coupons`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: couponForm.name,
          discount_type: couponForm.discount_type,
          discount_value: parseFloat(couponForm.discount_value),
          duration: couponForm.duration,
          duration_in_months: couponForm.duration_in_months ? parseInt(couponForm.duration_in_months) : null,
          max_redemptions: couponForm.max_redemptions ? parseInt(couponForm.max_redemptions) : null,
          promo_code: couponForm.promo_code || null,
          applies_to_tiers: couponForm.applies_to_tiers
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Coupon created successfully!");
        setShowForm(false);
        resetForm();
        fetchCoupons();
      } else {
        toast.error(data.detail || "Failed to create coupon");
      }
    } catch (error) {
      console.error("Create coupon error:", error);
      toast.error("Failed to create coupon");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCoupon = async (couponId, isActive) => {
    try {
      const response = await fetch(`${API}/payments/coupons/${couponId}/toggle`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !isActive })
      });

      if (response.ok) {
        toast.success(isActive ? "Coupon deactivated" : "Coupon activated");
        fetchCoupons();
      } else {
        toast.error("Failed to update coupon");
      }
    } catch (error) {
      toast.error("Failed to update coupon");
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      const response = await fetch(`${API}/payments/coupons/${couponId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Coupon deleted");
        setCoupons(prev => prev.filter(c => c.id !== couponId && c.stripe_coupon_id !== couponId));
      } else {
        toast.error("Failed to delete coupon");
      }
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleDeactivatePromoCode = async (promoId) => {
    try {
      const response = await fetch(`${API}/payments/promo-codes/${promoId}/deactivate`, {
        method: "POST",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Promo code deactivated");
        fetchCoupons();
      } else {
        toast.error("Failed to deactivate promo code");
      }
    } catch (error) {
      toast.error("Failed to deactivate promo code");
    }
  };

  const tierOptions = [
    { id: "foundations", label: "Foundations" },
    { id: "accelerator", label: "Advanced Accelerator" },
    { id: "elite", label: "Elite Coaching" }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold">Coupon Management</h2>
          <p className="text-gray-500 text-sm">Create and manage discount codes for your subscriptions</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center gap-2 justify-center"
          data-testid="create-coupon-btn"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "Create Coupon"}
        </button>
      </div>

      {/* Create Coupon Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-[#141B2D] border border-[#1E293B] rounded-xl overflow-hidden"
          >
            <div className="p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                New Coupon
              </h3>
              
              <form onSubmit={handleCreateCoupon} className="space-y-4">
                {/* Row 1: Name + Promo Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Coupon Name *</label>
                    <input
                      type="text"
                      value={couponForm.name}
                      onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                      placeholder="e.g., Summer Sale 20%"
                      className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white focus:border-primary outline-none"
                      data-testid="coupon-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Promo Code (Optional)</label>
                    <input
                      type="text"
                      value={couponForm.promo_code}
                      onChange={(e) => setCouponForm({ ...couponForm, promo_code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SUMMER20"
                      className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white uppercase focus:border-primary outline-none"
                      data-testid="coupon-promo-code-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Customer-facing code for checkout</p>
                  </div>
                </div>

                {/* Row 2: Discount Type + Value + Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Discount Type</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCouponForm({ ...couponForm, discount_type: "percent" })}
                        className={`flex-1 py-2.5 rounded-lg border transition-colors flex items-center justify-center gap-1.5 text-sm ${
                          couponForm.discount_type === "percent"
                            ? "bg-primary/20 border-primary text-primary"
                            : "bg-[#0B0F1A] border-[#1E293B] text-gray-400 hover:border-zinc-600"
                        }`}
                        data-testid="discount-type-percent"
                      >
                        <Percent className="w-4 h-4" />
                        %
                      </button>
                      <button
                        type="button"
                        onClick={() => setCouponForm({ ...couponForm, discount_type: "amount" })}
                        className={`flex-1 py-2.5 rounded-lg border transition-colors flex items-center justify-center gap-1.5 text-sm ${
                          couponForm.discount_type === "amount"
                            ? "bg-primary/20 border-primary text-primary"
                            : "bg-[#0B0F1A] border-[#1E293B] text-gray-400 hover:border-zinc-600"
                        }`}
                        data-testid="discount-type-amount"
                      >
                        <DollarSign className="w-4 h-4" />
                        $
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Discount Value * {couponForm.discount_type === "percent" ? "(%)" : "($)"}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        {couponForm.discount_type === "percent" ? "%" : "$"}
                      </span>
                      <input
                        type="number"
                        value={couponForm.discount_value}
                        onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                        placeholder={couponForm.discount_type === "percent" ? "20" : "10"}
                        min="1"
                        max={couponForm.discount_type === "percent" ? "100" : "10000"}
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg pl-8 pr-4 py-2.5 text-white focus:border-primary outline-none"
                        data-testid="coupon-value-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Duration</label>
                    <select
                      value={couponForm.duration}
                      onChange={(e) => setCouponForm({ ...couponForm, duration: e.target.value })}
                      className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white focus:border-primary outline-none"
                      data-testid="coupon-duration-select"
                    >
                      <option value="once">Once</option>
                      <option value="repeating">Repeating</option>
                      <option value="forever">Forever</option>
                    </select>
                  </div>
                </div>

                {/* Duration in Months (conditional) */}
                {couponForm.duration === "repeating" && (
                  <div className="md:w-1/3">
                    <label className="block text-sm text-gray-400 mb-2">Duration (months)</label>
                    <input
                      type="number"
                      value={couponForm.duration_in_months}
                      onChange={(e) => setCouponForm({ ...couponForm, duration_in_months: e.target.value })}
                      placeholder="3"
                      min="1"
                      max="36"
                      className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white focus:border-primary outline-none"
                      data-testid="coupon-months-input"
                    />
                  </div>
                )}

                {/* Row 3: Max Redemptions + Tiers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Max Redemptions (Optional)</label>
                    <input
                      type="number"
                      value={couponForm.max_redemptions}
                      onChange={(e) => setCouponForm({ ...couponForm, max_redemptions: e.target.value })}
                      placeholder="Leave empty for unlimited"
                      min="1"
                      className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2.5 text-white focus:border-primary outline-none"
                      data-testid="coupon-max-redemptions"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Applies to Tiers</label>
                    <div className="flex flex-wrap gap-2">
                      {tierOptions.map(tier => (
                        <button
                          key={tier.id}
                          type="button"
                          onClick={() => {
                            const tiers = couponForm.applies_to_tiers.includes(tier.id)
                              ? couponForm.applies_to_tiers.filter(t => t !== tier.id)
                              : [...couponForm.applies_to_tiers, tier.id];
                            setCouponForm({ ...couponForm, applies_to_tiers: tiers });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            couponForm.applies_to_tiers.includes(tier.id)
                              ? "bg-primary text-white"
                              : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
                          }`}
                          data-testid={`coupon-tier-${tier.id}`}
                        >
                          {tier.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-2 pt-4 border-t border-[#1E293B]">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-primary text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="submit-coupon-btn"
                  >
                    {saving ? "Creating..." : "Create Coupon"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    className="bg-[#1E293B] text-white px-4 py-2.5 rounded-lg hover:bg-[#2D3B4F] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Coupons List */}
      <div className="bg-[#141B2D] border border-[#1E293B] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#1E293B] flex justify-between items-center">
          <h3 className="font-semibold flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            Active Coupons
          </h3>
          <button
            onClick={fetchCoupons}
            disabled={loading}
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
            data-testid="refresh-coupons-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {coupons.length === 0 ? (
          <div className="p-12 text-center">
            <Tag className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold mb-2">No Coupons Yet</h3>
            <p className="text-gray-400 mb-4">Create your first discount coupon to attract new subscribers</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            >
              Create Coupon
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {coupons.map(coupon => (
              <CouponRow
                key={coupon.id || coupon.stripe_coupon_id}
                coupon={coupon}
                onToggle={handleToggleCoupon}
                onDelete={handleDeleteCoupon}
                onDeactivatePromo={handleDeactivatePromoCode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Individual coupon row component
 */
const CouponRow = ({ coupon, onToggle, onDelete, onDeactivatePromo }) => {
  const couponId = coupon.stripe_coupon_id || coupon.id;
  const isActive = coupon.valid !== false && coupon.is_active !== false;

  return (
    <div
      className={`p-4 hover:bg-[#0F1419] transition-colors ${!isActive ? "opacity-60" : ""}`}
      data-testid={`coupon-row-${couponId}`}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Coupon Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h4 className="font-heading font-bold text-white">{coupon.name || couponId}</h4>
            {coupon.promo_code && (
              <span className="bg-lime-400/20 text-lime-300 px-2 py-0.5 rounded text-xs font-mono">
                {coupon.promo_code}
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded ${
              isActive ? "bg-emerald-500/20 text-emerald-400" : "bg-[#2D3B4F] text-gray-500"
            }`}>
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            {/* Discount Display - Using correct Stripe fields */}
            <span className="flex items-center gap-1">
              {coupon.percent_off ? (
                <>
                  <Percent className="w-4 h-4" />
                  {coupon.percent_off}% off
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4" />
                  ${coupon.amount_off || 0} off
                </>
              )}
            </span>
            
            {/* Duration */}
            <span className="flex items-center gap-1 capitalize">
              <Clock className="w-4 h-4" />
              {coupon.duration === "repeating" 
                ? `${coupon.duration_in_months} months` 
                : coupon.duration}
            </span>
            
            {/* Usage Stats */}
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {coupon.max_redemptions 
                ? `${coupon.times_redeemed || 0}/${coupon.max_redemptions} used`
                : `${coupon.times_redeemed || 0} used`
              }
            </span>
          </div>

          {/* Promo Codes */}
          {coupon.promotion_codes?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {coupon.promotion_codes.map(pc => (
                <div 
                  key={pc.id} 
                  className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${
                    pc.active 
                      ? "bg-primary/10 border-primary/30 text-primary" 
                      : "bg-[#1E293B] border-[#1E293B] text-gray-500"
                  }`}
                >
                  <code className="font-mono text-sm">{pc.code}</code>
                  {pc.active && onDeactivatePromo && (
                    <button
                      onClick={() => onDeactivatePromo(pc.id)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Deactivate"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {onToggle && (
            <button
              onClick={() => onToggle(couponId, isActive)}
              className="p-2 hover:bg-[#1E293B] rounded-lg transition-colors"
              title={isActive ? "Deactivate" : "Activate"}
              data-testid={`toggle-coupon-${couponId}`}
            >
              {isActive ? (
                <ToggleRight className="w-5 h-5 text-emerald-400" />
              ) : (
                <ToggleLeft className="w-5 h-5 text-gray-500" />
              )}
            </button>
          )}
          <button
            onClick={() => onDelete(couponId)}
            className="p-2 hover:bg-red-500/10 rounded-lg text-gray-500 hover:text-red-400 transition-colors"
            title="Delete coupon"
            data-testid={`delete-coupon-${couponId}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponManager;
